import { 
  useContext, 
  useRef, 
  useState 
} from "react"

import { 
  ErrorPlaceholder,
  LoadingPlaceholder, 
  SimplePlaceholder, 
  SearchInput 
} from "@components"

import { UserContext } from "@context"
import { GET_REPORT_PARENT_ITINERARY } from "@graphql"
import { mySqlToLocal } from "@helpers"
import { useQuery } from "@apollo/client"
import { CalendarDaysIcon } from "@heroicons/react/24/outline"

import { 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableColumn, 
  TableHeader, 
  TableRow 
} from "@heroui/react"


import PropTypes from "prop-types"

const ParentItineraryList = ({ eventId, userIds, isVenue }) => {
  const { user } = useContext(UserContext)
  const [searchTerm, setSearchTerm] = useState("")
  const searchInputRef = useRef(null)

  const { loading, error, data } = useQuery(GET_REPORT_PARENT_ITINERARY, {
    variables: {
      user_ids: userIds,
      event_id: eventId,
      fetchPolicy: "cache-first",
      clientId: user.me.client.id,
      searchTerm,
      page: 1,
      perPage: 5000,
    },
  })

  const handleClearSearch = () => {
    searchInputRef.current.value = ""
    setSearchTerm("")
  }

  if (error) return <ErrorPlaceholder caption="Error loading itinerary" error={error} />

  return (
    <div>
      <SearchInput
        innerRef={searchInputRef}
        classNames="mb-3"
        debounceMilliSeconds={300}
        placeholder="Search by parent / student / class"
        handleChange={searchTerm => setSearchTerm(searchTerm)}
        handleClear={() => setSearchTerm("")}
      />

      {loading && <LoadingPlaceholder caption="Loading itinerary..." icon={CalendarDaysIcon} />}

      {!loading && data.reportParentItinerary.data.length === 0 && (
        <SimplePlaceholder icon={CalendarDaysIcon} title="No appointments found">
          <p className="mt-1 text-sm text-gray-500">
            You currently have no appointments.{" "}
            {searchTerm.length > 0 && (
              <span>
                Try a different search term.{" "}
                <Button className="mt-4 font-semibold" color="primary" fullWidth onPress={() => handleClearSearch()}>
                  Clear Search
                </Button>
              </span>
            )}
          </p>
        </SimplePlaceholder>
      )}

      {!loading && data.reportParentItinerary.data.length > 0 && (
        <Table isStriped isCompact removeWrapper aria-label="Parent Itinerary">
          <TableHeader>
            <TableColumn>Starts</TableColumn>
            <TableColumn>Ends</TableColumn>
            <TableColumn>Teacher</TableColumn>
            <TableColumn>Students</TableColumn>
            <TableColumn>Class</TableColumn>
            <TableColumn>Subject</TableColumn>
            <TableColumn className={`${isVenue ? "visible" : "hidden"}`}>Location</TableColumn>
          </TableHeader>

          <TableBody>
            {data.reportParentItinerary.data.map(item => (
              <TableRow key={item.id}>
                <TableCell>{mySqlToLocal(item.starts_at).format("HH:mm")}</TableCell>
                <TableCell>{mySqlToLocal(item.ends_at).format("HH:mm")}</TableCell>
                <TableCell>{item.group}</TableCell>
                <TableCell>{item.students}</TableCell>
                <TableCell>{item.lesson}</TableCell>
                <TableCell>{item.subject}</TableCell>
                <TableCell className={`${isVenue ? "visible" : "hidden"}`}>{item.location || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

ParentItineraryList.propTypes = {
  groupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  userIds: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isVenue: PropTypes.bool.isRequired,
}

export default ParentItineraryList
